<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";
import { DEFAULT_WORD_CLOUD_CONTRAST, WORD_CLOUD_CONTRASTS, WORD_CLOUD_LAYOUTS } from "../wordCloudPalettes";

export interface CloudWord {
  text: string;
  count: number;
  color?: string;
}

interface PlacedWord extends CloudWord {
  size: number;
  x: number;
  y: number;
  rotate: number;
  color: string;
}

const props = withDefaults(
  defineProps<{
    words: CloudWord[];
    colors: string[];
    respondents: number;
    contrast?: string;
    layout?: string;
  }>(),
  { contrast: DEFAULT_WORD_CLOUD_CONTRAST, layout: "horizontal" },
);

const WIDTH = 640;
const HEIGHT = 400;
// d3-cloud measures the words in a canvas, which needs the page font by name.
const FONT_FAMILY = getComputedStyle(document.body).fontFamily || "sans-serif";

const placed = ref<PlacedWord[]>([]);
const laying = ref(false);
const fit = ref("");
const MAX_FIT_SCALE = 1.6;

function seeded(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const hash = (text: string) => [...text].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 100003, 7);

const sized = computed(() => {
  const list = props.words;
  if (!list.length) return [];
  const range =
    WORD_CLOUD_CONTRASTS.find((c) => c.value === props.contrast) || WORD_CLOUD_CONTRASTS.find((c) => c.value === DEFAULT_WORD_CLOUD_CONTRAST)!;
  const counts = list.map((word) => word.count);
  const maxRoot = Math.sqrt(Math.max(...counts));
  const minRoot = Math.sqrt(Math.min(...counts));
  const spread = maxRoot - minRoot || 1;
  return list.map((word, idx) => ({
    ...word,
    size: Math.round(range.min + ((Math.sqrt(word.count) - minRoot) / spread) * (range.max - range.min)),
    color: word.color || props.colors[idx % Math.max(props.colors.length, 1)] || POPULATION_COLOR_PALETTES.slate[700],
  }));
});

const angles = computed(() => (WORD_CLOUD_LAYOUTS.find((l) => l.value === props.layout) || WORD_CLOUD_LAYOUTS[0]).angles);

let run = 0;

watch(
  [sized, angles],
  async ([words, rotations]) => {
    const current = ++run;
    if (!words.length) {
      placed.value = [];
      return;
    }
    laying.value = true;
    const { default: cloud } = await import("d3-cloud");
    if (current !== run) return;
    const random = seeded(words.reduce((acc, word) => acc + hash(word.text), words.length));
    const rotationFor = (text: string) => rotations[hash(text) % rotations.length];
    cloud<{ text: string; size: number; count: number; color: string; x?: number; y?: number; rotate?: number }>()
      .size([WIDTH, HEIGHT])
      .words(words.map((word) => ({ ...word })))
      .padding(3)
      .font(FONT_FAMILY)
      .fontWeight(600)
      .fontSize((word) => word.size)
      .rotate((word) => rotationFor(word.text))
      .random(random)
      .on("end", (tags) => {
        if (current !== run) return;
        const bounds = tags as { x?: number; y?: number; x0?: number; x1?: number; y0?: number; y1?: number }[];
        const left = Math.min(...bounds.map((tag) => (tag.x ?? 0) + (tag.x0 ?? 0)));
        const right = Math.max(...bounds.map((tag) => (tag.x ?? 0) + (tag.x1 ?? 0)));
        const top = Math.min(...bounds.map((tag) => (tag.y ?? 0) + (tag.y0 ?? 0)));
        const bottom = Math.max(...bounds.map((tag) => (tag.y ?? 0) + (tag.y1 ?? 0)));
        const scale = Math.min((WIDTH * 0.94) / Math.max(right - left, 1), (HEIGHT * 0.9) / Math.max(bottom - top, 1), MAX_FIT_SCALE);
        fit.value = `translate(${WIDTH / 2} ${HEIGHT / 2}) scale(${scale}) translate(${-(left + right) / 2} ${-(top + bottom) / 2})`;
        placed.value = tags.map((tag) => ({
          text: tag.text,
          count: tag.count,
          size: tag.size,
          color: tag.color,
          x: tag.x ?? 0,
          y: tag.y ?? 0,
          rotate: tag.rotate ?? 0,
        }));
        laying.value = false;
      })
      .start();
  },
  { immediate: true },
);

const dropped = computed(() => (laying.value ? 0 : Math.max(0, sized.value.length - placed.value.length)));

const hovered = ref<PlacedWord | null>(null);
const hoverPos = ref({ x: 0, y: 0 });
const wrapperEl = ref<HTMLElement | null>(null);

function trackHover(event: PointerEvent) {
  const rect = wrapperEl.value?.getBoundingClientRect();
  if (!rect) return;
  hoverPos.value = { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

const share = (count: number) => (props.respondents ? `${Math.round((count / props.respondents) * 100)}%` : "0%");
</script>

<template>
  <div ref="wrapperEl" class="relative w-full" @pointermove="trackHover" @pointerleave="hovered = null">
    <svg :viewBox="`0 0 ${WIDTH} ${HEIGHT}`" class="h-auto w-full" :class="{ 'opacity-50': laying }">
      <g :transform="fit">
        <text
          v-for="word in placed"
          :key="word.text"
          :x="word.x"
          :y="word.y"
          :transform="`rotate(${word.rotate}, ${word.x}, ${word.y})`"
          text-anchor="middle"
          class="cursor-default transition-opacity"
          :class="{ 'opacity-40': hovered && hovered.text !== word.text }"
          :style="{ fontSize: `${word.size}px`, fontFamily: FONT_FAMILY, fontWeight: 600, fill: word.color }"
          @pointerenter="hovered = word"
          @pointerleave="hovered = null">
          {{ word.text }}
        </text>
      </g>
    </svg>

    <div
      v-if="hovered"
      class="pointer-events-none absolute z-10 rounded-md border bg-background px-2.5 py-1.5 text-xs shadow-md"
      :style="{ left: `${hoverPos.x + 12}px`, top: `${hoverPos.y + 12}px` }">
      <div class="font-medium">{{ hovered.text }}</div>
      <div class="tabular-nums text-muted-foreground">{{ hovered.count }} respondents · {{ share(hovered.count) }}</div>
    </div>

    <p v-if="dropped > 0" class="mt-1 text-center text-2xs text-muted-foreground">
      {{ dropped }} smaller {{ dropped === 1 ? "word" : "words" }} did not fit; lower the max words or the size contrast.
    </p>
  </div>
</template>
